// MapContainer.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Map from "./Map";
import { Marker, InfoWindow } from "@react-google-maps/api";
import { v4 as uuidv4 } from "uuid";
import "./map.css";
import { RootContext } from "../../contexts/root-context";
import substationMarkerIcon from "../../assets/new-dr.gif";
import substationsData from "./dummy-substations.json";

// Helper function to format date
const convertUtcToLocalString = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch (error) {
    return "N/A";
  }
};

export default function MapContainer() {
  const [map, setMap] = useState(null);
  const [substations, setSubstations] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [selectedSubstation, setSelectedSubstation] = useState(null);
  const { setORList } = useContext(RootContext);

  useEffect(() => {
    // Load substations from JSON file
    setSubstations(substationsData);
    if (typeof setORList === 'function') {
      setORList(substationsData.map(loc => loc.name));
    }
  }, []);

  useEffect(() => {
    if (map) {
      fitMapToBounds();
    }
  }, [map, substations]);

  const fitMapToBounds = () => {
    if (!map || !substations.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    substations.forEach((substation) => {
      bounds.extend(substation.position);
    });

    map.fitBounds(bounds);
    window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
      if (map.getZoom() > 5) {
        map.setZoom(5);
      }
    });
  };
  // Ensure zoom doesn't go in too far

  const navigate = useNavigate();
  const handleMarkerClick = (marker, substation) => {
    setSelectedMarker(marker.position);
    setSelectedSubstation(substation);
    setInfoWindowOpen(true);
  };

  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
    setSelectedSubstation(null);
    setInfoWindowOpen(false);
  };

  const gotoDashboard = (substationName, substationId) => {
    localStorage.setItem("selectedSubstation", substationName);
    localStorage.setItem("selectedSubstationId", substationId);

    // Navigate to dashboard
    navigate("/dashboard");
  };

  return (
    <div className="map-container">
      <Map setMap={setMap}>
        {substations.map((substation) => (
          <Marker
            key={uuidv4()}
            position={substation.position}
            title={substation.name}
            icon={{
              url: substationMarkerIcon,
              ...(window.google && window.google.maps
                ? {
                  scaledSize: new window.google.maps.Size(40, 40), // marker size
                  labelOrigin: new window.google.maps.Point(20, 20) // center of the 40x40 marker
                }
                : {})
            }}
            label={{
              text: substation.disturbanceRecords.toString(),
              color: "white",
              fontWeight: "bold",
              fontSize: "14px"
            }}

            onClick={() => handleMarkerClick({ position: substation.position }, substation)}
          />
        ))}

        {selectedMarker && selectedSubstation && (
          <InfoWindow
            position={selectedMarker}
            onCloseClick={handleInfoWindowClose}
            visible={infoWindowOpen}
          >
            <div className="info-box">
              <div className="info-header">
                <h3>Substation Details</h3>
              </div>
              <div className="info-content">
                <p><strong>Name:</strong> {selectedSubstation.name}</p>
                <p><strong>Operation Area:</strong> {selectedSubstation.operationArea}</p>
                <p><strong>Type:</strong> {selectedSubstation.type}</p>
                <p><strong>Disturbance Records:</strong> {selectedSubstation.disturbanceRecords}</p>
                <p><strong>Last Updated:</strong> {convertUtcToLocalString(selectedSubstation.lastUpdatedOn)}</p>
              </div>
              <div className="info-footer">
                <button
                  className="view-dashboard-btn"
                  onClick={() => gotoDashboard(selectedSubstation.name, selectedSubstation.substationId)}
                >
                  View Dashboard
                </button>
              </div>
            </div>
          </InfoWindow>
        )}
      </Map>
    </div>
  );
}